import { PhoneCall, Ruler, PaintBucket, CheckCircle2, ArrowUpRight } from "lucide-react";
import { SITE } from "@/lib/site";

const STEPS = [
  {
    icon: PhoneCall,
    title: "Kontakt",
    text: "Ring eller skicka ett meddelande. Vi svarar inom kort med första frågorna.",
    href: SITE.phoneHref,
    cta: "Ring oss nu",
    external: true,
  },
  {
    icon: Ruler,
    title: "Hembesök & offert",
    text: "Vi kommer hem till dig, mäter, lyssnar på dina önskemål och tar fram en tydlig offert.",
    href: "#kontakt",
    cta: "Begär offert",
    external: false,
  },
  {
    icon: PaintBucket,
    title: "Vi utför arbetet",
    text: "Skarp planering, rätt material och säkert hantverk – från start till mål.",
  },
  {
    icon: CheckCircle2,
    title: "Godkännande",
    text: "Vi går igenom resultatet tillsammans. Du betalar när du är 100% nöjd.",
  },
];

export const Process = () => {
  const handleAnchorClick = (e, href) => {
    if (!href || href.startsWith("tel:") || href.startsWith("mailto:")) return;
    if (href.startsWith("#")) {
      e.preventDefault();
      const el = document.getElementById(href.slice(1));
      if (el) el.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <section data-testid="process-section" className="py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-5 md:px-10">
        <div className="max-w-3xl mb-12">
          <div className="inline-flex items-center gap-2 font-display font-bold text-xs uppercase tracking-[0.18em] text-[#D92525]">
            <span className="w-8 h-[2px] bg-[#D92525]" /> 02 — Så funkar det
          </div>
          <h2 className="font-display text-4xl sm:text-5xl md:text-6xl font-black tracking-tighter text-white mt-3 leading-[1]">
            Från idé till
            <br />
            <span className="text-[#FFB3B3]">färdigt resultat.</span>
          </h2>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {STEPS.map((s, i) => {
            const Icon = s.icon;
            const isClickable = !!s.href;
            const commonClass =
              "relative bg-white border-2 border-[#0B132B] p-7 transition-all block h-full";
            const interactiveClass = isClickable
              ? "cursor-pointer hover:shadow-[8px_8px_0_#D92525] hover:-translate-y-1 focus:outline-none focus:shadow-[8px_8px_0_#D92525] focus:-translate-y-1"
              : "hover:shadow-[8px_8px_0_#D92525] hover:-translate-y-1";

            const inner = (
              <>
                <div className="font-display text-7xl font-black text-[#0B132B]/5 absolute top-2 right-4 leading-none">
                  0{i + 1}
                </div>
                <div className="flex items-center justify-between">
                  <Icon size={28} strokeWidth={2.2} className="text-[#D92525]" />
                  {isClickable && (
                    <ArrowUpRight
                      size={20}
                      strokeWidth={2.4}
                      className="text-[#0B132B]/40 group-hover:text-[#D92525] group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all"
                    />
                  )}
                </div>
                <h3 className="font-display text-xl md:text-2xl font-bold mt-5 text-[#0B132B]">
                  {s.title}
                </h3>
                <p className="font-body text-[#4B5563] mt-2 text-sm leading-relaxed">
                  {s.text}
                </p>
                {isClickable && (
                  <div className="mt-4 inline-flex items-center gap-1.5 font-display font-bold text-xs tracking-[0.15em] uppercase text-[#D92525]">
                    {s.cta}
                    <span aria-hidden>→</span>
                  </div>
                )}
              </>
            );

            if (isClickable) {
              return (
                <a
                  key={i}
                  href={s.href}
                  onClick={(e) => handleAnchorClick(e, s.href)}
                  data-testid={`process-step-${i}`}
                  aria-label={`${s.title} — ${s.cta}`}
                  className={`group ${commonClass} ${interactiveClass}`}
                >
                  {inner}
                </a>
              );
            }

            return (
              <div
                key={i}
                data-testid={`process-step-${i}`}
                className={`${commonClass} ${interactiveClass}`}
              >
                {inner}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
